// Local, read-only combat-island projection for the GM initiative picker.
//
// The browser already owns the live encounterArr and FT3D owns the compiled shape, runtime door
// overlay, and complete GM physical-light grid. Previewing from those retained truth objects avoids
// recompiling the same encounter through a second HTTP request. Janus still resolves the island
// again transactionally when Start combat is posted; this module never authorizes or writes combat.

import { floorBandForPosition } from '../encounter-creation-engine/campaign-placement-authority';
import {
    type Ft3dCellLightGrid,
} from '../encounter-creation-engine/ft3d-cell-light';
import { encounterCreatureField, encounterField, itemsCol } from '../shared/array-types';
import { COMBAT_ALERT_HEARING_RADIUS_FT } from '../shared/combat-alert-radius';
import {
    admittedObscuredSources,
    canSeeWithSenses,
    collectObscuredSources,
    hasStatus,
    physicalLightAt,
    position,
    shapeIsDerivedExteriorFallback,
    sightRayZ,
    sightWallBlocksForViewer,
    targetGrounded,
    wallBlocks,
    type PerceptionShape,
    type PerceptionSource,
} from '../encounter-map/ft3d/perception';
import { segmentHit3d, type ShapeSegmentHit3d } from '../../../ft3d/engine/ft3d-shape-core.js';

// TeamType catalog (includes/additions.php TeamType::LIST).
export const TEAM_FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3';
export const TEAM_ENEMY = '9399856B-2132-7574-1684-91139A2831DD';
export const TEAM_NEUTRAL = 'BBF5D6A6-FB9B-E07D-0497-B60EC68D2EBD';
const STATUS = Object.freeze({
    deafened: '9529EEA7-63A9-C572-ECA9-F523E87C58A8',
});

export type ClientCombatShape = PerceptionShape;

export interface ClientCombatTruth {
    shape: ClientCombatShape;
    lightGrid?: Ft3dCellLightGrid | null;
}

export interface ClientCombatResolution {
    participantIds: string[];
    reasons: Record<string, string>;
}

type EncounterRow = Record<string, unknown> & { encounter_creatures?: unknown };
type ObscuredSource = PerceptionSource;

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function scalar(value: unknown): unknown {
    if (Array.isArray(value)) return value[0];
    if (value && typeof value === 'object' && 0 in (value as Record<number, unknown>)) {
        return (value as Record<number, unknown>)[0];
    }
    return value;
}

function finite(value: unknown, fallback = 0): number {
    const number = Number(scalar(value));
    return Number.isFinite(number) ? number : fallback;
}

function rowId(row: EncounterRow): string {
    return String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').trim().toUpperCase();
}

/** A soft-deleted roster row (encounter_creature_deleted set — PHP `!empty()`) is no creature of the scene: it never seeds, joins, extends the island
 *  or makes a sound (lane N5f — coordinator Codex round 8: a deleted row used to pull in whoever it could see, and was only dropped as a joiner). */
function rowDeleted(row: EncounterRow): boolean {
    const v = scalar(encounterCreatureField(row.encounter_creatures, 'encounter_creature_deleted'));
    return !(v === undefined || v === null || v === '' || v === 0 || v === '0' || v === false);
}

function itemType(row: EncounterRow): string {
    return String(itemsCol(row, 'item_type') ?? encounterCreatureField(row.encounter_creatures, 'item_type') ?? '').trim().toLowerCase();
}

function floorAtRow(shape: ClientCombatShape, row: EncounterRow): number {
    const at = position(row);
    return floorBandForPosition(shape.boxes, at.x, at.y ?? 0, at.z);
}

function hitIsClosedDoor(shape: ClientCombatShape, hit: ShapeSegmentHit3d): boolean {
    const span = hit.axis === 'x' ? hit.y : hit.x;
    return (shape.openings ?? []).some(opening => opening.plane?.axis === hit.axis
        && Math.abs(finite(opening.plane.at, Number.POSITIVE_INFINITY) - hit.at) <= 1e-6
        && span >= finite(opening.span?.min, Number.POSITIVE_INFINITY) - 1e-6
        && span <= finite(opening.span?.max, Number.NEGATIVE_INFINITY) + 1e-6
        && hit.z >= finite(opening.z?.min, Number.POSITIVE_INFINITY) - 1e-6
        && hit.z <= finite(opening.z?.max, Number.NEGATIVE_INFINITY) + 1e-6
        && opening.passable === false && opening.transparent === false);
}

function distanceBetween(left: EncounterRow, right: EncounterRow): number {
    const a = position(left);
    const b = position(right);
    return Math.hypot(a.x - b.x, (a.y ?? 0) - (b.y ?? 0), a.z - b.z);
}

function detectionBetween(
    shape: ClientCombatShape,
    lightGrid: Ft3dCellLightGrid | null | undefined,
    detector: EncounterRow,
    target: EncounterRow,
    obscuredSources: ObscuredSource[],
    decodedLight: Map<string, Uint8Array>,
    hearingRadius = COMBAT_ALERT_HEARING_RADIUS_FT,
): 'sight' | 'hearing' | null {
    if (Math.abs(floorAtRow(shape, detector) - floorAtRow(shape, target)) > 0.01) return null;
    const detectorAt = position(detector);
    const targetAt = position(target);
    const distance = distanceBetween(detector, target);
    const light = physicalLightAt(lightGrid, shape, target, decodedLight);
    if (canSeeWithSenses(detector, target, light, distance, admittedObscuredSources(shape, target, obscuredSources),
        targetGrounded(shape, target), { allowWithinReach: false })
        && !sightWallBlocksForViewer(shape, detector, detectorAt, targetAt, sightRayZ(shape, detector))) return 'sight';
    if (hasStatus(detector, STATUS.deafened) || distance > hearingRadius) return null;
    let throughClosedDoor = false;
    if (shape.boxes.length > 0 && !shapeIsDerivedExteriorFallback(shape)) {
        // Keep tracing past each closed door: a solid wall behind it still blocks (owner 2026-09-17; server
        // twin ft_combat_sound_path()). Several doors halve once.
        const destination = { x: targetAt.x, y: targetAt.y ?? 0, z: targetAt.z + 2.5 };
        let source = { x: detectorAt.x, y: detectorAt.y ?? 0, z: detectorAt.z + 2.5 };
        let sealed = true;
        for (let guard = 0; guard < 8; guard++) {
            const hit = segmentHit3d(shape, source, destination, { mode: 'sight' });
            if (!hit) { sealed = false; break; }
            if (!hitIsClosedDoor(shape, hit)) return null;
            throughClosedDoor = true;
            const length = Math.hypot(destination.x - source.x, destination.y - source.y);
            const step = Math.min(1, Number(hit.t) + 0.05 / Math.max(0.05, length));
            if (step >= 1) { sealed = false; break; }
            source = {
                x: source.x + (destination.x - source.x) * step,
                y: source.y + (destination.y - source.y) * step,
                z: source.z + (destination.z - source.z) * step,
            };
        }
        if (sealed) return null;
    }
    return distance <= hearingRadius * (throughClosedDoor ? 0.5 : 1) ? 'hearing' : null;
}

/**
 * Exact mirror of the server's check_opposing_team(): only friend-vs-enemy counts. Neutral, absent
 * or unknown teams are never opposing. Exported so the combat-start prompt scores the same pairs
 * the engine would.
 */
export function opposingTeams(left: EncounterRow, right: EncounterRow): boolean {
    const leftTeam = String(encounterCreatureField(left.encounter_creatures, 'team') ?? '').toUpperCase();
    const rightTeam = String(encounterCreatureField(right.encounter_creatures, 'team') ?? '').toUpperCase();
    return (leftTeam === TEAM_FRIEND || leftTeam === TEAM_ENEMY)
        && (rightTeam === TEAM_FRIEND || rightTeam === TEAM_ENEMY)
        && leftTeam !== rightTeam;
}

/** A hostile action's SOUND (R-131 J3 — the browser twin of PHP's ft_combat_noise_event(), lane N5e): its source row (the acting creature) and
 *  how far it carries (encounter-map/ft3d/combat-noise.ts — the loudness class's radius). */
export interface ClientCombatNoise { sourceId: string; radiusFt: number }

export function resolveClientCombatIsland(
    encounter: Record<string, unknown>,
    truth: ClientCombatTruth,
    seedIds: string[],
    excludedIds: string[] = [],
    /** R-131 J3: the sound events of committed hostile actions (ft_combat_grow_participants' `$noise_event` branch): a candidate also joins when it
     *  HEARS a source within its radius — the hearing channel (walls block, a closed door halves, deafened hears nothing), not sight. */
    noise: ClientCombatNoise[] = [],
): ClientCombatResolution {
    const excluded = new Set(excludedIds.map(id => id.trim().toUpperCase()));
    const rows = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
    const eligible = new Map<string, EncounterRow>();
    for (const candidate of rows) {
        const row = recordOf(candidate) as EncounterRow | null;
        if (!row) continue;
        const id = rowId(row);
        if (!id || excluded.has(id) || rowDeleted(row) || finite(encounterCreatureField(row.encounter_creatures, 'encounter_creature_dead')) === 1) continue;
        if (itemType(row) !== 'lair' && finite(encounterCreatureField(row.encounter_creatures, 'encounter_creature_private')) === 1) continue;
        eligible.set(id, row);
    }
    const participants = new Set(seedIds.map(id => id.trim().toUpperCase()).filter(id => eligible.has(id)));
    const reasons: Record<string, string> = Object.fromEntries([...participants].map(id => [id, 'seed']));
    if (participants.size === 0) return { participantIds: [], reasons };
    const obscuredSources = collectObscuredSources(encounter);
    const decodedLight = new Map<string, Uint8Array>();
    const pairMemo = new Map<string, 'sight' | 'hearing' | null>();
    const detect = (detectorId: string, targetId: string, radius = COMBAT_ALERT_HEARING_RADIUS_FT): 'sight' | 'hearing' | null => {
        const key = `${detectorId}|${targetId}|${radius}`;
        if (!pairMemo.has(key)) pairMemo.set(key, detectionBetween(
            truth.shape,
            truth.lightGrid,
            eligible.get(detectorId)!,
            eligible.get(targetId)!,
            obscuredSources,
            decodedLight,
            radius,
        ));
        return pairMemo.get(key) ?? null;
    };
    // The noise sources are rows of the roster (any live one — PHP passed the caster row itself, eligible or not); a source the page does not hold,
    // or a soft-deleted row, carries nothing.
    const byIdAll = new Map<string, EncounterRow>();
    for (const candidate of rows) { const row = recordOf(candidate) as EncounterRow | null; if (row && rowId(row) && !rowDeleted(row)) byIdAll.set(rowId(row), row); }
    const sounds = noise.map(n => ({ id: n.sourceId.trim().toUpperCase(), radius: Number(n.radiusFt) }))
        .filter(n => byIdAll.has(n.id) && Number.isFinite(n.radius) && n.radius > 0);
    const hears = (candidateId: string, source: { id: string; radius: number }): boolean => {
        const key = `${candidateId}|noise:${source.id}|${source.radius}`;
        if (!pairMemo.has(key)) pairMemo.set(key, detectionBetween(truth.shape, truth.lightGrid, eligible.get(candidateId)!, byIdAll.get(source.id)!,
            obscuredSources, decodedLight, source.radius));
        return pairMemo.get(key) === 'hearing';
    };
    let changed: boolean;
    do {
        changed = false;
        for (const [candidateId, candidate] of eligible) {
            if (participants.has(candidateId)) continue;
            const controllerId = String(encounterCreatureField(candidate.encounter_creatures, 'controller_encounter_creature_id') ?? '').trim().toUpperCase();
            let reason: string | null = controllerId && participants.has(controllerId) ? 'summon' : null;
            if (!reason && itemType(candidate) === 'lair') {
                for (const participantId of participants) {
                    const participant = eligible.get(participantId);
                    if (participant && itemType(participant) !== 'lair' && detect(candidateId, participantId, 120) !== null) {
                        reason = 'lair';
                        break;
                    }
                }
            }
            // the hostile action's sound (PHP checked it before the baseline channel and only on HEARING — a candidate that sees the source joins
            // below by sight of a member, as the source always is one)
            if (!reason && sounds.some(source => source.id !== candidateId && hears(candidateId, source))) reason = 'hearing';
            if (!reason) {
                for (const participantId of participants) {
                    const participant = eligible.get(participantId);
                    if (!participant) continue;
                    const channel = detect(candidateId, participantId);
                    if (channel === 'sight' || (channel === 'hearing' && opposingTeams(participant, candidate))) {
                        reason = channel;
                        break;
                    }
                }
            }
            if (reason) {
                participants.add(candidateId);
                reasons[candidateId] = reason;
                changed = true;
            }
        }
    } while (changed);
    return { participantIds: [...participants], reasons };
}

/**
 * Is this creature a member of the encounter's OPEN combat? Mirrors the server's
 * ft_creature_matches_active_combat() (combat_membership.php): membership is the creature's own
 * encounter_creatures.combat_id matching encounters.active_combat_id — a LOCAL fact, never "is any
 * combat open in this encounter".
 *
 * The legacy lane matches the server's too: a combat opened before the membership migration has
 * in_combat truth but no UUIDs anywhere. Only when NO row in the encounter carries a combat_id do
 * we treat every resident as a member; once any tag exists we fail closed rather than mixing an
 * untagged row into a tagged combat.
 */
export function creatureIsInActiveCombat(
    encounter: Record<string, unknown> | null | undefined,
    row: EncounterRow | null | undefined,
): boolean {
    if (!encounter || !row) return false;

    const activeCombatId = String(encounterField(encounter, 'active_combat_id') ?? '').trim().toUpperCase();
    const rowCombatId = String(encounterCreatureField(row.encounter_creatures, 'combat_id') ?? '').trim().toUpperCase();

    if (activeCombatId !== '') return rowCombatId === activeCombatId;

    if (Number(encounterField(encounter, 'in_combat') ?? 0) !== 1) return false;

    const rows = (encounter.encounter_creatures_arr as EncounterRow[] | undefined) ?? [];
    if (!Array.isArray(rows) || rows.length === 0) return false;
    const anyTagged = rows.some(candidate => (
        String(encounterCreatureField(candidate?.encounter_creatures, 'combat_id') ?? '').trim() !== ''
    ));
    return !anyTagged;
}

/**
 * Is the sight line between two creatures blocked by geometry? The eye ray starts at each
 * creature's own sight height (sightRayZ), so an upper-story row is tested from its own floor.
 *
 * APPROXIMATION, deliberately. The server's contest cover samples NINE rays across the target
 * volume and calls the contest an automatic loss only at the 'blocked' tier — every ray stopped
 * (ft_contest_cover_context → ft_contest_cover_outcome). This is the CENTRE ray alone, so it
 * reports blocked slightly more often than the server would. Its only consumer is the combat-start
 * prompt, where the cost of erring is one extra button the GM can ignore, and the cost of erring
 * the other way is silently missing a contest the engine already scored as lost. Do not reuse it
 * for anything mechanical without building the full nine-sample version.
 */
export function creatureSightLineBlocked(
    shape: ClientCombatShape | null | undefined,
    from: EncounterRow | null | undefined,
    to: EncounterRow | null | undefined,
): boolean {
    if (!shape || !Array.isArray(shape.boxes) || shape.boxes.length === 0 || !from || !to) return false;
    return wallBlocks(shape, position(from), position(to), sightRayZ(shape, from));
}
